/**
 * Purpose: Client-side JPEG base64 encoding shared by face capture and product scan.
 * Inputs: File, Blob, or HTMLVideoElement; optional maxWidth / jpegQuality / aspectRatio.
 * Outputs: Raw base64 strings without a data-URL prefix.
 */

// Default resize cap for camera frames — balances quality vs upload size.
export const IMAGE_BASE64_DEFAULT_MAX_WIDTH = 1280
// JPEG quality used by canvas.toBlob for all encoded frames.
export const IMAGE_BASE64_DEFAULT_JPEG_QUALITY = 0.85

// Optional overrides for callers that need different dimensions or compression.
export type ImageBase64Options = {
  maxWidth?: number
  jpegQuality?: number
  /**
   * Target width÷height (e.g. `3 / 4` for diary selfie slots). When set, the
   * frame is center-cropped to this ratio before encode so thumbnails match the
   * slot instead of stretching a landscape frame into a portrait box.
   */
  aspectRatio?: number
}

/** Source rect for an optional center-crop to `aspectRatio` (width ÷ height). */
export function videoFrameSourceRect(
  videoWidth: number,
  videoHeight: number,
  aspectRatio?: number,
): { sx: number; sy: number; sw: number; sh: number } {
  // No crop requested — draw the full intrinsic frame.
  if (!aspectRatio || aspectRatio <= 0) {
    return { sx: 0, sy: 0, sw: videoWidth, sh: videoHeight }
  }

  const videoRatio = videoWidth / videoHeight
  // Wider than target — trim left/right; taller — trim top/bottom.
  if (videoRatio > aspectRatio) {
    const sw = Math.round(videoHeight * aspectRatio)
    return { sx: Math.round((videoWidth - sw) / 2), sy: 0, sw, sh: videoHeight }
  }
  if (videoRatio < aspectRatio) {
    const sh = Math.round(videoWidth / aspectRatio)
    return { sx: 0, sy: Math.round((videoHeight - sh) / 2), sw: videoWidth, sh }
  }
  return { sx: 0, sy: 0, sw: videoWidth, sh: videoHeight }
}

/** Strips the `data:*;base64,` prefix from a data URL, returning raw base64. */
export function dataUrlToBase64(dataUrl: string): string {
  // Data URLs always separate metadata from payload with a comma.
  const comma = dataUrl.indexOf(',')
  return comma >= 0 ? dataUrl.slice(comma + 1) : dataUrl
}

/** Reads a File as JPEG base64 via FileReader (no resize). */
export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    // FileReader delivers a data URL string on success.
    reader.onload = () => {
      const result = reader.result
      if (typeof result !== 'string') {
        reject(new Error('FileReader did not return a string'))
        return
      }
      // Strip the prefix so API routes receive raw base64 only.
      resolve(dataUrlToBase64(result))
    }
    reader.onerror = () => reject(reader.error ?? new Error('FileReader failed'))
    reader.readAsDataURL(file)
  })
}

/** Reads a Blob as JPEG base64 via FileReader. */
export function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      const result = reader.result
      if (typeof result !== 'string') {
        reject(new Error('FileReader did not return a string'))
        return
      }
      resolve(dataUrlToBase64(result))
    }
    reader.onerror = () => reject(reader.error ?? new Error('FileReader failed'))
    reader.readAsDataURL(blob)
  })
}

/**
 * Captures the current video frame to JPEG base64 using canvas `toBlob`
 * (async path — avoids blocking the main thread on large frames).
 */
export async function videoFrameToBase64(
  video: HTMLVideoElement,
  options: ImageBase64Options = {},
): Promise<string> {
  // Fall back to shared defaults when the caller omits options.
  const maxWidth = options.maxWidth ?? IMAGE_BASE64_DEFAULT_MAX_WIDTH
  const jpegQuality = options.jpegQuality ?? IMAGE_BASE64_DEFAULT_JPEG_QUALITY

  // videoWidth/Height stay 0 until loadedmetadata — callers must guard first.
  if (!video.videoWidth || !video.videoHeight) {
    throw new Error('Video frame not ready')
  }

  // Optional center-crop so the JPEG matches the UI slot (e.g. diary 3:4).
  const { sx, sy, sw, sh } = videoFrameSourceRect(
    video.videoWidth,
    video.videoHeight,
    options.aspectRatio,
  )

  // Scale down wide frames while preserving the (possibly cropped) aspect ratio.
  const scale = Math.min(1, maxWidth / sw)
  const canvas = document.createElement('canvas')
  canvas.width = Math.round(sw * scale)
  canvas.height = Math.round(sh * scale)
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('Canvas 2d context unavailable')
  ctx.drawImage(video, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height)

  // toBlob is async — wrap in a Promise so callers can await the result.
  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error('toBlob returned null'))),
      'image/jpeg',
      jpegQuality,
    )
  })
  // Reuse blobToBase64 so every path returns the same raw-base64 shape.
  return blobToBase64(blob)
}
